import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/Login";
import { resetPassword } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import PasswordRequirements, { passwordIsStrong } from "@/components/PasswordRequirements";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const strong = passwordIsStrong(password);
  const matches = password.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Reset link is missing the token. Open the link from your email again.");
      return;
    }
    if (!strong) {
      toast.error("Password doesn't meet the requirements");
      return;
    }
    if (!matches) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Password reset — please log in");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="flex items-start gap-3" data-testid="reset-error-no-token">
          <WarningCircle size={22} weight="fill" className="text-rose-500 shrink-0 mt-0.5" />
          <div>
            <h1 className="font-display text-2xl font-black tracking-tight">Invalid reset link</h1>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              This link is missing its token. Open the email link again, or request a new one.
            </p>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm text-slate-950 font-semibold underline">
              Request a new reset link
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div className="flex items-start gap-3" data-testid="reset-success-card">
          <CheckCircle size={22} weight="fill" className="text-emerald-500 dark:text-mint shrink-0 mt-0.5" />
          <div>
            <h1 className="font-display text-2xl font-black tracking-tight">Password updated</h1>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              You'll be redirected to the login page in a moment.
            </p>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-black tracking-tight">Choose a new password</h1>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">
        Pick something memorable but strong. We'll sign you out of any other sessions.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
            New password
          </Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="reset-password-input"
            autoFocus
          />
          <PasswordRequirements password={password} testid="reset-password-requirements" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
            Confirm password
          </Label>
          <PasswordInput
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat password"
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="reset-password-confirm"
          />
          {confirm && !matches && (
            <div className="text-[11px] text-rose-600 mt-1" data-testid="reset-password-mismatch">
              Passwords don't match.
            </div>
          )}
        </div>
        <Button
          type="submit"
          disabled={loading || !strong || !matches}
          className="w-full h-11 bg-slate-950 hover:bg-slate-800 text-white rounded-md font-medium"
          data-testid="reset-submit-btn"
        >
          {loading ? "Saving…" : (
            <span className="flex items-center gap-2 justify-center">
              Update password <ArrowRight size={14} weight="bold" />
            </span>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
